import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  FollowUpLinkType,
  FollowUpReasonsByType,
  FollowUpType,
} from '@app/modules/follow-ups/follow-ups.types';
import { CreateFollowUpsAction } from '@app/modules/follow-ups/store/follow-ups.actions';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableConfiguration,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { of } from 'rxjs';

@Component({
  selector: 'app-follow-ups-create-dialog',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    SelectComponent,
    SelectOptionComponent,
    DataTableComponent,
  ],
  templateUrl: './follow-ups-create-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FollowUpsCreateDialogComponent implements OnInit {
  private store = inject(Store);
  private modalRef = inject(NgbActiveModal);
  private fb = inject(FormBuilder);

  @Input() linkIds: string[];
  @Input() linkType: FollowUpLinkType;
  @Input() entities: { id: string }[];

  followUpReasons = signal<string[]>([]);
  followUpTypes = signal<FollowUpType[]>(
    Object.values(FollowUpType).filter(
      (type) => type !== FollowUpType.ReportRevision
    )
  );
  loading = signal<boolean>(false);

  form: FormGroup;

  followUpEntitiesDataTable: DataTableConfiguration;

  constructor() {
    effect(() => {
      if (this.followUpReasons().length === 0) {
        this.form.get('reason').disable();
      } else {
        this.form.get('reason').enable();
      }
    });
  }

  ngOnInit() {
    this.form = this.fb.group({
      type: ['', Validators.required],
      reason: ['', Validators.required],
    });

    this.form.get('type').valueChanges.subscribe((type) => {
      if (type) {
        this.followUpReasons.set(FollowUpReasonsByType[type] ?? []);
        this.form.get('reason').reset();
      }
    });

    this.followUpEntitiesDataTable = {
      title: '',
      tableHeadClasses: '',
      selectionMode: DataTableSelectionType.NONE,
      columns: [
        {
          label: 'followUps.modal.entitiesTable.entity',
          property: 'id',
        },
      ],
      rows$: of(this.entities),
    };
  }

  close() {
    this.modalRef.close();
  }

  submit() {
    this.loading.set(true);
    this.store
      .dispatch(
        new CreateFollowUpsAction(
          this.linkIds,
          this.linkType,
          this.form.value.type,
          this.form.value.reason
        )
      )
      .subscribe(() => {
        this.loading.set(false);
        this.close();
      });
  }

  protected readonly Object = Object;
}
